"use client";

import React, { useState, useEffect } from "react";
import { Phone, Mail, Linkedin, Twitter, Facebook, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative py-2 text-primary font-default font-bold text-sm uppercase tracking-wider transition-colors hover:text-secondary after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-secondary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 py-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-2 py-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block py-3 px-4 text-primary font-default font-bold text-lg uppercase rounded-md hover:bg-accent transition-colors" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="259786" id="header" className={`sticky top-0 z-50 flex flex-col w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-[12px] shadow-md" : "bg-background"}`}>
      {}
      <div className="w-full bg-primary text-primary-foreground py-2 hidden sm:block">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl flex justify-between items-center text-xs font-default tracking-wide">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Phone className="size-3 text-chart-3" />
              <span>330.516.9401</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="size-3 text-chart-3" />
              <span>info@thehywm.com</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="#" className="hover:text-secondary transition-colors" aria-label="LinkedIn">
              <Linkedin className="size-3.5" />
            </Link>
            <Link to="#" className="hover:text-secondary transition-colors" aria-label="Twitter">
              <Twitter className="size-3.5" />
            </Link>
            <Link to="#" className="hover:text-secondary transition-colors" aria-label="Facebook">
              <Facebook className="size-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {}
      <div className={`w-full transition-all duration-300 ${isScrolled ? "py-3 border-b border-border" : "py-5"}`}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl flex justify-between items-center">
          {}
          <Link to="/" className="flex-shrink-0 z-10">
            <WvcLogo className="h-7 w-auto text-primary" />
          </Link>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4 z-10">
            <Button className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase tracking-widest rounded-md px-6 shadow-md transition-all hover:-translate-y-1 animate-pulse">
              Get a Quote
            </Button>
            
            <Button variant="ghost" size="icon" className="md:hidden p-2 text-primary hover:bg-accent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden w-full bg-background border-b border-border px-4 pb-6 shadow-lg">
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <Button className="w-full mt-4 bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase tracking-widest rounded-md py-6 text-lg shadow-md" onClick={() => setIsMobileMenuOpen(false)}>
            Get a Quote
          </Button>
        </div>}
    </section>;
}